import { useQuery } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";
import { CheckCircle2, CloudUpload, Printer, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { usePos } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import { call } from "@/lib/api";

import { useConnectivity } from "./connectivity";
import { ConflictCard } from "./ConflictCard";
import { useSyncState, useSyncWorker } from "./OfflineRuntime";
import { isOld } from "./opQueue";

/**
 * 同期状況 (T07-33): what is still waiting to be uploaded (kept on this tablet even when the app is
 * closed), what the last upload did, the sales that got their real receipt number (print the real
 * receipt), and the 要確認 cards a manager has to close. 「今すぐ同期」 uploads at once (the
 * register also does it by itself when the connection is back).
 */
export function SyncScreen() {
  const { t, i18n } = useTranslation();
  const { db, api } = usePos();
  const worker = useSyncWorker();
  const sync = useSyncState();
  const connectivity = useConnectivity();
  const online = connectivity.state === "online";
  const queued = useLiveQuery(() => db.opQueue.orderBy("createdAt").toArray(), [db]) ?? [];
  const results =
    useLiveQuery(() => db.syncResults.orderBy("syncedAt").reverse().limit(50).toArray(), [db]) ??
    [];
  const reviews = results.filter((r) => r.needsReview === 1);
  const uploaded = results.filter((r) => r.needsReview === 0 && r.receiptNo);
  const server = useQuery({
    queryKey: ["pos-sync", "detail", "status"],
    queryFn: () => call(() => api.GET("/v1/pos/sync/status")),
    enabled: online,
    refetchInterval: online && queued.length ? 10_000 : false,
  });
  const time = (value: number | string | undefined) =>
    value === undefined
      ? "—"
      : new Intl.DateTimeFormat(i18n.resolvedLanguage ?? "ja", {
          month: "numeric",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "Asia/Tokyo",
        }).format(new Date(value));

  return (
    <section className="flex h-full flex-col gap-4 overflow-y-auto p-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{t("sync.title")}</h1>
        <button
          type="button"
          onClick={() => void worker?.run()}
          disabled={!online || sync.running || queued.length === 0}
          className="ml-auto flex min-h-pos-touch items-center gap-2 rounded-md bg-primary px-6 text-lg font-semibold text-primary-foreground disabled:opacity-50"
        >
          <CloudUpload aria-hidden className="size-6" />
          {sync.running ? t("sync.running") : t("sync.now")}
        </button>
      </header>
      {!online ? <p className="rounded-md bg-yellow-100 p-3">{t("sync.offline_note")}</p> : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <section aria-labelledby="sync-queue" className="flex flex-col gap-2">
          <h2 id="sync-queue" className="text-lg font-semibold">
            {t("sync.queue_title", { count: queued.length })}
          </h2>
          {queued.length === 0 ? (
            <p className="flex items-center gap-2 text-muted-foreground">
              <CheckCircle2 aria-hidden className="size-5 text-pos-ok" />
              {t("sync.queue_empty")}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {queued.map((op) => (
                <li key={op.idempotencyKey} className="rounded-md border p-3">
                  <div className="flex items-center gap-3">
                    <strong>{op.summary?.tempReceiptNo ?? op.kind}</strong>
                    <span className="text-muted-foreground">{time(op.createdAt)}</span>
                    {op.summary?.total !== undefined ? (
                      <MoneyText amount={op.summary.total} className="ml-auto" />
                    ) : null}
                  </div>
                  {op.attempts > 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t("sync.waiting_retry", { count: op.attempts })}
                    </p>
                  ) : null}
                  {isOld(op) ? (
                    <p role="alert" className="flex items-center gap-1 text-pos-problem">
                      <TriangleAlert aria-hidden className="size-4" />
                      {t("sync.old_op")}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {sync.last ? (
            <p className="text-sm text-muted-foreground">
              {t("sync.last_run", {
                time: time(sync.lastRunAt ?? undefined),
                ok: sync.last.ok + sync.last.duplicate,
                review: sync.last.conflict + sync.last.rejected,
                pending: sync.last.pending,
              })}
              {sync.last.error ? ` — ${t("sync.last_error")}` : ""}
            </p>
          ) : null}
          {server.data?.lastBatch ? (
            <p className="text-sm text-muted-foreground">
              {t("sync.server_last", {
                time: time(server.data.lastBatch.receivedAt),
                count: server.data.lastBatch.opCount,
              })}
            </p>
          ) : null}
        </section>

        <section aria-labelledby="sync-review" className="flex flex-col gap-2">
          <h2 id="sync-review" className="text-lg font-semibold">
            {t("sync.review_title", { count: reviews.filter((r) => !r.resolvedAt).length })}
          </h2>
          {reviews.length === 0 ? (
            <p className="text-muted-foreground">{t("sync.review_empty")}</p>
          ) : (
            reviews.map((record) => <ConflictCard key={record.idempotencyKey} record={record} />)
          )}
        </section>
      </div>

      <section aria-labelledby="sync-done" className="flex flex-col gap-2">
        <h2 id="sync-done" className="text-lg font-semibold">
          {t("sync.done_title")}
        </h2>
        {uploaded.length === 0 ? (
          <p className="text-muted-foreground">{t("sync.done_empty")}</p>
        ) : (
          <ul className="grid gap-2 md:grid-cols-2">
            {uploaded.map((r) => (
              <li
                key={r.idempotencyKey}
                className="flex min-h-12 items-center gap-3 rounded-md border px-3"
              >
                <span>
                  {r.tempReceiptNo} → <strong>{r.receiptNo}</strong>
                </span>
                {r.total !== null && r.total !== undefined ? <MoneyText amount={r.total} /> : null}
                {r.saleId ? (
                  <Link
                    to={`/receipts/${r.saleId}`}
                    className="ml-auto flex min-h-10 items-center gap-1 rounded-md border px-3"
                  >
                    <Printer aria-hidden className="size-4" />
                    {t("sync.print_real")}
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
